import React, { useEffect, useState } from 'react'
import {
  Form,
  Button,
  Row,
  Col,
  Select,
  Upload,
  Divider,
  Space,
  DatePicker,
  Table,
  Modal,
  Spin,
} from 'antd'
import {
  UploadOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import AdminHeader from '../../CommonComponents/AdminHeader'
import AdminFooter from '../../CommonComponents/AdminFooter'
import axiosInstance from '../../../Utils/axiosInstance'
import {
  getChildrenList,
  IMG_URL,
  getExtensionEntry,
  getExtensionEntryById,
  deleteExtensionEntry,
  saveExtensionEntry,
} from '../../APIComponents/AdminApi'

const { Option } = Select

const ExtensionEntry = () => {
  const [form] = Form.useForm()

  const [childrenList, setChildrenList] = useState([])
  const [tableData, setTableData] = useState([])
  const [singleData, setSingleData] = useState([])

  const [showForm, setShowForm] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [recordId, setRecordId] = useState(null)

  const [loading, setLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [editLoading, setEditLoading] = useState(false)

  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  const [file, setFile] = useState(null)
  const [fileList, setFileList] = useState([])
  const [extensionDate, setExtensionDate] = useState(null)

  const [viewModal, setViewModal] = useState(false)
  const [viewData, setViewData] = useState(null)

  const handleView = (record) => {
    setViewData({
      studentName: getStudentName(record.reg_id),
      records: record.records, // all entries
    })
    setViewModal(true)
  }

  // ================= GROUP =================
  const groupByStudent = (data) => {
    const map = {}

    data.forEach((item) => {
      if (!map[item.reg_id]) {
        map[item.reg_id] = {
          reg_id: item.reg_id,
          total: 0,
          records: [],
        }
      }

      map[item.reg_id].total += 1
      map[item.reg_id].records.push(item)
    })

    // Sort records inside each group (latest first)
    Object.values(map).forEach((group) => {
      group.records.sort((a, b) => new Date(b.date) - new Date(a.date))
    })

    //  SORT GROUPS BASED ON reminder_flag
    const sortedGroups = Object.values(map).sort((a, b) => {
      const flagA = a.records?.[0]?.reminder_flag || 0
      const flagB = b.records?.[0]?.reminder_flag || 0

      // Higher flag first (3 → 2 → 1 → 0)
      return flagB - flagA
    })

    return sortedGroups
  }

  // ================= FETCH =================
  useEffect(() => {
    fetchChildren()
    fetchTableData()
  }, [])

  const fetchChildren = async () => {
    try {
      const res = await axiosInstance.get(getChildrenList)
      setChildrenList(res.data || [])
    } catch {
      toast.error('Failed to fetch children')
    }
  }

  const fetchTableData = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(getExtensionEntry)
      setTableData(groupByStudent(res.data || []))
    } catch {
      toast.error('Failed to fetch data')
    } finally {
      setTableLoading(false)
    }
  }

  const getStudentName = (id) => {
    const c = childrenList.find((x) => x.id === id)
    return c ? `${c.fname} ${c.lname}` : '-'
  }

  // ================= STUDENT CHANGE =================
  const handleStudentChange = async (value) => {
    try {
      setEditLoading(true)

      const res = await axiosInstance.get(`${getExtensionEntryById}/${value}`)

      console.log('API DATA:', res.data) // 🔍 DEBUG

      const sorted = (res.data || []).sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      )

      setSingleData(sorted)
    } catch (err) {
      console.log(err)
      toast.error('Failed to load student data')
    } finally {
      setEditLoading(false)
    }
  }

  // ================= FILE =================
  const validateFile = (file) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
    ]

    const isAllowedType = allowedTypes.includes(file.type)

    if (!isAllowedType) {
      toast.error('Only JPG, PNG, and PDF files are allowed!')
      return Upload.LIST_IGNORE
    }

    const isLt3M = file.size / 1024 / 1024 < 3

    if (!isLt3M) {
      toast.warn('File must be smaller than 3MB!')
      return Upload.LIST_IGNORE
    }

    return false // prevent auto upload
  }

  const getReminderText = (flag) => {
    switch (flag) {
      case 1:
        return '15 Days Remaining'
      case 2:
        return '8 Days Remaining'
      case 3:
        return '5 Days Remaining'
      default:
        return '-'
    }
  }

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      const res = await axiosInstance.delete(
        `${deleteExtensionEntry}/${deleteId}`,
      )

      if (res.data === 'DELETED') {
        toast.success('Deleted')
        fetchTableData()
      }
    } catch {
      toast.error('Delete failed')
    } finally {
      setDeleteModal(false)
    }
  }

  // ================= SUBMIT =================
  // const handleFinish = async (values) => {
  //   try {
  //     if (!file || !extensionDate) {
  //       toast.warn('Select date & file')
  //       return
  //     }

  //     setLoading(true)

  //     const formData = new FormData()
  //     formData.append('reg_id', values.childName)
  //     formData.append('date', dayjs(extensionDate).format('YYYY-MM-DD'))
  //     formData.append('namuna_eighteen_file', file)

  //     const res = await axiosInstance.post(saveExtensionEntry, formData)

  //     if (res.data === 'SAVED') {
  //       toast.success('Data Saved Successfully')
  //       handleCancel()
  //       fetchTableData()
  //     } else if (res.data === 'ALREADY_EXIST_ON_THIS_DATE') {
  //       toast.warn('Data already exist for selected date')
  //     }
  //   } catch {
  //     toast.error('Submit failed')
  //   } finally {
  //     setLoading(false)
  //   }
  // }

  const handleFinish = async (values) => {
    try {
      if (!file || !extensionDate) {
        toast.warn('Select date & file')
        return
      }

      setLoading(true)

      const formData = new FormData()
      formData.append('reg_id', values.childName)
      formData.append('date', dayjs(extensionDate).format('YYYY-MM-DD'))
      formData.append('namuna_eighteen_file', file)

      const res = await axiosInstance.post(saveExtensionEntry, formData)

      if (res.data === 'SAVED') {
        toast.success('Data Saved Successfully')
        handleCancel()
        fetchTableData()
      } else if (res.data === 'ALREADY_EXIST_ON_THIS_DATE') {
        toast.warn('Data already exist for selected date')
      }
    } catch {
      toast.error('Submit failed')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setFile(null)
    setFileList([])
    setExtensionDate(null)
    setSingleData([])
    setShowForm(false)
    setIsEdit(false)
  }

  // ================= TABLE =================
  const columns = [
    {
      title: 'Student',
      render: (_, r) => getStudentName(r.reg_id),
    },
    {
      title: 'Total Entries',
      dataIndex: 'total',
    },
    {
      title: 'Last Date',
      render: (_, r) => dayjs(r.records?.[0]?.date).format('DD-MM-YYYY'),
    },
    {
      title: 'Reminder',
      render: (_, r) => getReminderText(r.records?.[0]?.reminder_flag),
    },
    {
      title: 'Action',
      render: (_, r) => (
        <Button icon={<EyeOutlined />} onClick={() => handleView(r)}>
          View
        </Button>
      ),
    },
  ]

  return (
    <>
      <AdminHeader />

      <div className="student-form-section container-fluid">
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-2">Extension Entry</h3>
          </div>

          {!showForm && (
            <>
              <Row justify="end" className="mb-3">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowForm(true)}
                >
                  Add
                </Button>
              </Row>

              <Table
                dataSource={tableData}
                columns={columns}
                rowKey="reg_id"
                loading={tableLoading}
                className="custom-table shadow rounded border"
                style={{ overflowX: 'auto' }}
              />
            </>
          )}

          {showForm && (
            <Spin spinning={editLoading}>
              <div className="row student-form-container rounded shadow  py-2">
                <Form form={form} layout="vertical" onFinish={handleFinish}>
                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={24} md={10}>
                      <Form.Item
                        name="childName"
                        label="Student"
                        rules={[{ required: true }]}
                      >
                        <Select
                          size="large"
                          onChange={handleStudentChange}
                          disabled={isEdit}
                        >
                          {childrenList.map((child) => (
                            <Option key={child.id} value={child.id}>
                              {child.fname} {child.mname} {child.lname} (Reg No:{' '}
                              {child.reg_no})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={7}>
                      <Form.Item label="Date">
                        <DatePicker
                          size="large"
                          value={extensionDate}
                          onChange={setExtensionDate}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={7}>
                      <Form.Item label="File">
                        <Upload
                          beforeUpload={(file) => {
                            const isValid = validateFile(file)
                            if (isValid === Upload.LIST_IGNORE)
                              return Upload.LIST_IGNORE

                            setFile(file) // ✅ store actual file
                            setFileList([file]) // optional (for UI)

                            return false // prevent auto upload
                          }}
                          fileList={fileList}
                          onRemove={() => {
                            setFile(null)
                            setFileList([])
                          }}
                          accept=".jpg,.jpeg,.png,.pdf"
                          maxCount={1}
                          listType="picture"
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload (Max 3MB)
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Table
                    dataSource={singleData}
                    pagination={false}
                    rowKey="id"
                    columns={[
                      {
                        title: 'Date',
                        render: (r) => dayjs(r.date).format('DD-MM-YYYY'),
                      },
                      {
                        title: 'File',
                        render: (r) => (
                          <a
                            href={IMG_URL + r.namuna_eighteen_file}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </a>
                        ),
                      },
                    ]}
                    className="custom-table shadow rounded border"
                    style={{ overflowX: 'auto' }}
                  />

                  <Divider />

                  <Row justify="end">
                    <Space>
                      <Button onClick={handleCancel}>Cancel</Button>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                      >
                        {isEdit ? 'Update' : 'Submit'}
                      </Button>
                    </Space>
                  </Row>
                </Form>
              </div>
            </Spin>
          )}

          {/* =========== DELETE MODAL ================ */}
          <Modal
            open={deleteModal}
            onOk={handleDelete}
            onCancel={() => setDeleteModal(false)}
          >
            Confirm delete?
          </Modal>

          {/* =========== VIEW MODAL ================ */}
          <Modal
            open={viewModal}
            onCancel={() => setViewModal(false)}
            footer={null}
            width={700}
          >
            {viewData && (
              <>
                <h3>{viewData.studentName}</h3>

                <Table
                  dataSource={viewData.records}
                  pagination={false}
                  rowKey="id"
                  columns={[
                    {
                      title: 'Date',
                      render: (r) => dayjs(r.date).format('DD-MM-YYYY'),
                    },
                    {
                      title: 'Reminder',
                      render: (r) => getReminderText(r.reminder_flag),
                    },
                    {
                      title: 'File',
                      render: (r) => (
                        <a
                          href={IMG_URL + r.namuna_eighteen_file}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View
                        </a>
                      ),
                    },
                  ]}
                  className="custom-table shadow rounded border"
                  style={{ overflowX: 'auto' }}
                />
              </>
            )}
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default ExtensionEntry
